import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Collapse,
  Alert,
  AlertTitle,
} from '@mui/material';
import { AppDispatch, AppStore } from '../../../store/AppStore';
import { EActionType, RegExPatterns, ECommonDialogType } from '../../../data/Constants';
import { useDispatch } from 'react-redux';
import {
  getMultiNumericFeature,
  createMultiNumericFeature,
  updateMultiNumericFeature,
} from '../../../store/states/FeatureSlice';
import { closeDialog, openDialog } from '../../../store/states/DialogSlice';

import type {
  IManipulationProps,
  IMultiNumericFeatureData,
  IMultiNumericHandleCreateProps,
  IMultiNumericHandleResponseProps,
  IMultiNumericLoadFormDataProps,
  IMultiNumericValidationProps,
} from '../../../../types';

/**
 * This function call the get multinumeric api and set the data to the state to populate the values
 * @param { load}
 */
function loadInitialData(props: IMultiNumericLoadFormDataProps) {
  const {
    initialData,
    action,
    id,
    dispatch,
    setInitialMultiNumericData,
    setFormData,
    setAlertMessage,
    t,
  } = props;

  if (action === EActionType.Edit && id) {
    dispatch(getMultiNumericFeature({ searchCode: id }))
      .then((response) => {
        if (response?.payload?.data) {
          const fetchedData: IMultiNumericFeatureData = response.payload.data[0];
          setInitialMultiNumericData(fetchedData);
          setFormData(fetchedData);
        }
      })
      .catch(() => {
        setAlertMessage(t('messages.fail.get'));
      });
  } else if (action === EActionType.Create) {
    setInitialMultiNumericData(initialData);
  }
}

/**
 * This method call the create or update api to add or update the multi numeric feature to the db
 * @param {IMultiNumericCreateProps} props input props
 * @returns {void}
 */
async function callCreateOrUpdateApi(props: IMultiNumericHandleCreateProps): Promise<void> {
  const { action, formData, t, dispatch, setAlertMessage } = props;

  const request = {
    request: {
      ...formData,
      name: formData.name.trimEnd(),
      code: formData.code.trim(),
    },
  };

  if (action === EActionType.Edit) {
    const updateResponse: { payload } = await dispatch(updateMultiNumericFeature(request));

    handleApiResponse({
      response: updateResponse.payload,
      failMessage: t('messages.fail.update'),
      successMessage: t('messages.success.update'),
      dispatch,
      setAlertMessage,
    });
  } else {
    const createResponse: { payload } = await dispatch(createMultiNumericFeature(request));
    handleApiResponse({
      response: createResponse.payload,
      failMessage: t('messages.fail.create'),
      successMessage: t('messages.success.create'),
      dispatch,
      setAlertMessage,
    });
  }
}

/** This method check the create or update response and shows the success or error message
 * @param {IMultiNumericHandleApiResponseProps} props input params
 * @returns {void }
 */
function handleApiResponse(props: IMultiNumericHandleResponseProps) {
  const { response, failMessage, successMessage, dispatch, setAlertMessage } = props;
  const multiNumericFeature = AppStore.getState().multiNumeric;

  if (response?.data) {
    dispatch(
      getMultiNumericFeature({
        page: multiNumericFeature.currentPage,
        limit: multiNumericFeature.recordsPerPage,
        searchCode: multiNumericFeature.searchKey,
      })
    );

    // Open success dialog
    dispatch(openDialog({ type: ECommonDialogType.Success, data: successMessage }));
  } else {
    const message = response?.error.data?.message ?? failMessage;
    setAlertMessage(message);
  }
}

/**
 * This method checks the field validation and show the error message
 * @param  {IMultiNumericValidationProps} props input params
 * @returns {void}
 */
function formValidation(props: IMultiNumericValidationProps): void {
  const {
    formData,
    initialMultiNumericData,
    setDisplayNameErrorMessage,
    setDisplayCodeErrorMessage,
    setDisableButton,
  } = props;

  const stringValidations = {
    name: { min: 4, max: 40 },
    code: { min: 4, max: 40 },
  };

  //Remove the space start  and end of the value
  const name = formData.name.trimEnd();
  const code = formData.code.trimEnd();

  //Name should be greater than 4 characters and less than or equal to 40 characters
  const validName =
    name.length >= stringValidations.name.min && name.length <= stringValidations.name.max;

  //Code should be greater than 4 characters and less than or equal to 40 characters
  //And it should accept only alpha numeric values
  const validCode =
    code.length >= stringValidations.code.min &&
    code.length <= stringValidations.code.max &&
    RegExPatterns.AplhaNumericHyphenUnderscore.test(code);

  //Check whether the data is modified or not
  const detailsUpdated =
    JSON.stringify(initialMultiNumericData) !== JSON.stringify({ ...formData, name });

  setDisplayNameErrorMessage(!validName);
  setDisplayCodeErrorMessage(!validCode);
  setDisableButton(!validName || !validCode || !detailsUpdated);
}

/**
 * This component renders the dialog with multi numeric feature to create or update the feature
 * @param {IManipulationProps} props the input params
 * @returns {React.ReactElement} returns the component
 */
export const MultiNumericManipulation = (props: IManipulationProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const action = props.action;
  const initialData: IMultiNumericFeatureData = {
    name: '',
    code: '',
    isLocal: false,
    isActive: true,
  };

  const [formData, setFormData] = useState<IMultiNumericFeatureData>(initialData);
  const [initialMultiNumericData, setInitialMultiNumericData] =
    useState<IMultiNumericFeatureData>(initialData);
  const [disableButton, setDisableButton] = useState(true);
  const [displayCodeErrorMessage, setDisplayCodeErrorMessage] = useState<boolean>(false);
  const [displayNameErrorMessage, setDisplayNameErrorMessage] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');

  /**
   * This function call the get api to load the value on the dialog
   */
  useEffect(() => {
    loadInitialData({
      initialData,
      action,
      id: props.id,
      dispatch,
      setInitialMultiNumericData,
      setFormData,
      setAlertMessage,
      t,
    });
  }, []);

  /**
   * This useEffect trigger when change any values on the dialog
   * This will validate  the field values and show the error message
   */
  useEffect(() => {
    formValidation({
      formData,
      initialMultiNumericData,
      setDisplayNameErrorMessage,
      setDisplayCodeErrorMessage,
      setDisableButton,
    });
  });

  /**
   * This function trigger when change the field values to update the value to the state
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const name = event.target.name;
    let value: string | boolean;
    if (name === 'isLocal' || name === 'isActive') {
      value = event.target.checked;
    } else {
      value = event.target.value;
    }
    setFormData((values) => ({ ...values, [name]: value }));
  };

  /**
   * This function trigger when click on the save or create button on the dialog to save the data
   * @param {React.MouseEventHandler<HTMLButtonElement>} event
   */
  const handleSubmit = (event: React.MouseEventHandler<HTMLButtonElement>): void => {
    event?.preventDefault();
    setAlertMessage('');
    callCreateOrUpdateApi({ action, formData, t, dispatch, setAlertMessage });
  };

  /**
   * This function is used to close error message on the dialog when click on the close icon.
   * @param {React.SyntheticEvent}_event
   * @param {string} reason
   * @returns
   */
  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertMessage('');
  };

  /**
   * This function trigger when click on cancel button on the dialog to close the dialog
   */
  const handleCloseDialog = () => {
    dispatch(closeDialog());
    setAlertMessage('');
  };

  return (
    <Box className="manipulationBox pt-1">
      <Box className="manipulationForm">
        <Collapse in={!!alertMessage} className="mb-05em">
          <Alert className="errorMessage" severity="error" onClose={handleClose}>
            {alertMessage}
          </Alert>
        </Collapse>

        <TextField
          name="code"
          label={t('labels.code')}
          required
          variant="outlined"
          size="small"
          disabled={action === EActionType.Edit || !formData.isActive}
          fullWidth
          value={formData.code || ''}
          onChange={(e) => {
            handleChange(e);
          }}
          slotProps={{
            htmlInput: { 'data-testid': 'multinumeric-code-input' },
          }}
        />
        <br />
        <Collapse in={displayCodeErrorMessage && formData.code.length > 0}>
          <Alert className="errorMessage" severity="error">
            <AlertTitle>{t('messages.codeErrorMessage')} </AlertTitle>
          </Alert>
        </Collapse>
        <br />

        <TextField
          name="name"
          label={t('labels.name')}
          required
          variant="outlined"
          size="small"
          disabled={!formData.isActive}
          fullWidth
          value={formData.name || ''}
          onChange={(e) => {
            handleChange(e);
          }}
          slotProps={{
            htmlInput: { 'data-testid': 'multinumeric-name-input' },
          }}
        />
        <br />
        <Collapse in={displayNameErrorMessage && formData.name.length > 0}>
          <Alert className="errorMessage" severity="error">
            <AlertTitle>{t('messages.nameErrorMessage')} </AlertTitle>
          </Alert>
        </Collapse>
        <fieldset className="inputFieldset w-100 ">
          <legend className="inputLegend" id="others">
            {t('labels.others')}
          </legend>
          {
            <FormControlLabel
              value={formData.isLocal}
              checked={!!formData.isLocal}
              control={
                <Checkbox
                  slotProps={{ 'data-testid': 'multinumeric-isLocal-input' }}
                  disabled={!formData.isActive}
                />
              }
              label={t('labels.isLocal')}
              labelPlacement="end"
              onChange={(e) => {
                handleChange(e);
              }}
              name="isLocal"
            />
          }

          {!initialMultiNumericData.isActive && action === EActionType.Edit ? (
            <FormControlLabel
              value={formData.isActive}
              checked={!!formData.isActive}
              control={<Checkbox />}
              label={t('labels.isActive')}
              labelPlacement="end"
              onChange={(e) => {
                handleChange(e);
              }}
              name="isActive"
            />
          ) : null}
        </fieldset>
        <br />

        <Box className="formActionButtons">
          <Button
            data-testid="multinumeric-button-submit"
            variant="contained"
            size="medium"
            onClick={handleSubmit}
            className="text-capitalize"
            disabled={disableButton}
          >
            {action === EActionType.Create ? t('button.create') : t('button.save')}
          </Button>
          <Button
            data-testid="multinumeric-button-cancel"
            size="medium"
            onClick={() => handleCloseDialog()}
            className="text-capitalize"
          >
            {t('button.cancel')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
